import { z } from 'zod';
import { PostStatusSchema, BrandOrSalesSchema } from './posts';
import {
  ApprovalStageSchema,
  PublicApprovalImageSchema,
  SubmitDecisionSchema,
  PublicApprovalCommentSchema,
} from './approvals';

/**
 * Client calendar (tesztkör-4, 2026-09-22; on the approval page since
 * 2026-09-29): shows every post the client has ever been sent as a calendar
 * (only what went out in a batch — never a merely published post), with the full history of each post —
 * when it went out, what the client decided, what was said — and lets the
 * client decide on the still-open ones right there.
 */

export const PublicCalendarQuerySchema = z.object({
  year: z.coerce.number().int().min(2020).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
});

/** Internal preview: the calendar as one recipient (by address) of the hotel sees it. */
export const InternalCalendarQuerySchema = PublicCalendarQuerySchema.extend({
  email: z.string().trim().email().max(320),
  /**
   * Send-before preview (2026-09-30, Nóra): the posts of a batch not created
   * yet, shown as if already sent. Comma-separated in the query string; the
   * cap is the batch's own (`CreateApprovalBatchSchema.postIds`).
   */
  draftPostIds: z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    )
    .pipe(z.array(z.string().guid()).min(1).max(60))
    .optional(),
  draftStage: ApprovalStageSchema.optional(),
}).refine((q) => !q.draftPostIds || !!q.draftStage, {
  message: 'draftStage is required with draftPostIds',
  path: ['draftStage'],
});
export type InternalCalendarQuery = z.infer<typeof InternalCalendarQuerySchema>;
export type PublicCalendarQuery = z.infer<typeof PublicCalendarQuerySchema>;

export const PublicCalendarTimelineEventSchema = z.object({
  at: z.string().datetime(),
  kind: z.enum(['sent', 'decision', 'comment', 'status', 'published']),
  /** Short Hungarian label ("Kiküldve jóváhagyásra", "Jóváhagyta", …). */
  label: z.string(),
  /** Free text: the client's comment, the note, the platform, … */
  detail: z.string().nullable(),
  platform: z.enum(['fb', 'ig']).nullable(),
  /** Who did it, as far as the client may know: "Ön" / the colleague / null. */
  author: z.string().nullable(),
});
export type PublicCalendarTimelineEvent = z.infer<typeof PublicCalendarTimelineEventSchema>;

export const PublicCalendarPendingSchema = z.object({
  /** Null only in the send-before preview: the batch does not exist yet. */
  batchId: z.string().guid().nullable(),
  stage: ApprovalStageSchema,
  /** Platforms the client already answered on in this round. */
  decidedPlatforms: z.array(z.enum(['fb', 'ig'])),
  /** True once a whole-post decision exists in this round. */
  wholeDecided: z.boolean(),
});
export type PublicCalendarPending = z.infer<typeof PublicCalendarPendingSchema>;

export const PublicCalendarPostSchema = z.object({
  id: z.string().guid(),
  scheduledFor: z.string().datetime(),
  status: PostStatusSchema,
  brandOrSales: BrandOrSalesSchema,
  category: z.string().nullable(),
  topic: z.object({ title: z.string(), angle: z.string().nullable() }).nullable(),
  images: z.array(PublicApprovalImageSchema),
  contents: z.array(
    z.object({
      platform: z.enum(['fb', 'ig']),
      body: z.string(),
      hashtags: z.array(z.string()),
      sourceUrl: z.string().nullable(),
      attachmentUrl: z.string().nullable(),
    }),
  ),
  /** Non-null while the post waits for the client's decision. */
  pending: PublicCalendarPendingSchema.nullable(),
  timeline: z.array(PublicCalendarTimelineEventSchema),
});
export type PublicCalendarPost = z.infer<typeof PublicCalendarPostSchema>;

export const PublicCalendarSchema = z.object({
  hotelName: z.string(),
  year: z.number().int(),
  month: z.number().int(),
  /** Every month that has at least one visible post, ascending. */
  availableMonths: z.array(z.object({ year: z.number().int(), month: z.number().int() })),
  /** True when the shown month is before the current Budapest month: read-only. */
  archived: z.boolean(),
  /** Whose link this is — the calendar tells the client in whose name they comment. */
  viewer: z.object({ email: z.string().nullable(), name: z.string().nullable() }),
  posts: z.array(PublicCalendarPostSchema),
});
export type PublicCalendar = z.infer<typeof PublicCalendarSchema>;

/** Same bodies as the batch link: the calendar routes the decision to the open batch. */
export const PublicCalendarDecisionSchema = SubmitDecisionSchema;
export type PublicCalendarDecision = z.infer<typeof PublicCalendarDecisionSchema>;
export const PublicCalendarCommentSchema = PublicApprovalCommentSchema;
export type PublicCalendarComment = z.infer<typeof PublicCalendarCommentSchema>;
